import type { ResearchCapacityAcceptance, ResearchCapacityTerms } from "@nodus/api/gen/nodus_dev_v1";
import type { EstimateView, OfferingLike } from "@nodus/ui";
import type { Scope, WorkspaceSpec } from "./model";

export type ResearchOffering = OfferingLike & { spec?: OfferingLike["spec"] & { researchCapacity?: ResearchCapacityTerms | null } };
const POLICY = "research-artifacts-v1";
const DIGEST = "sha256:560418784f7f18c95f437ebddbbcc87897b8f99b1821192ba8f439ec51bb6c05";
export const RESEARCH_TERMS_URL = "https://nodus-compute.ai/terms/research-artifacts-v1.txt";

/** An unknown research contract must never appear as ordinary capacity. */
export function isResearchOffering(offering: OfferingLike): boolean {
  return Boolean(offering.spec && "researchCapacity" in offering.spec);
}

/** This console accepts only the reviewed whole-node contract; later policies need their own review. */
export function researchTerms(offering?: OfferingLike): ResearchCapacityTerms | undefined {
  const spec = (offering as ResearchOffering | undefined)?.spec;
  const terms = spec?.researchCapacity;
  return spec?.category === "GPU" && spec.accelerator?.id === "B300" && spec.count === 8 && spec.interruptible === true &&
    terms?.policy === POLICY && terms.termsDigest === DIGEST && terms.termsURL === RESEARCH_TERMS_URL &&
    typeof terms.disclosure === "string" && terms.disclosure.length > 0 && terms.disclosure.length <= 4096 ? terms : undefined;
}

export function researchAccepted(value?: ResearchCapacityAcceptance): boolean {
  return value?.policy === POLICY && value.termsDigest === DIGEST;
}

/** Acceptance applies to this exact reviewed launch, never a restored unsubmitted draft or a changed input. */
export function researchContext(scope: Scope, userID: string, name: string, spec: WorkspaceSpec, offering?: OfferingLike): string | undefined {
  const terms = researchTerms(offering);
  if (!offering || !terms || spec.mode === "Spot" || spec.resources?.gpu?.count !== 8 || spec.placement?.offerings?.length !== 1 ||
    spec.placement.offerings[0] !== offering.metadata.name || spec.placement.pool || spec.placement.interruptible !== "Allow") return undefined;
  return JSON.stringify([userID, scope.org, scope.project, name, offering.metadata.name, terms, { ...spec, researchCapacity: undefined }]);
}

/** A successful estimate must show the same contract as the exact offer the customer accepted. */
export function researchEstimateMatches(estimate: EstimateView | undefined, offering?: OfferingLike): boolean {
  const terms = researchTerms(offering);
  const preview = estimate?.placementPreview as (EstimateView["placementPreview"] & { researchCapacity?: ResearchCapacityTerms });
  return Boolean(terms && preview?.offering === offering?.metadata.name && preview.interruptible === true &&
    preview.researchCapacity?.policy === terms.policy && preview.researchCapacity.termsDigest === terms.termsDigest &&
    preview.researchCapacity.termsURL === terms.termsURL && preview.researchCapacity.disclosure === terms.disclosure);
}
